import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  BacklogCursor,
  BacklogDigest,
  BacklogEvent,
  BacklogEventCounts,
  BacklogEventsResponse,
  ProjectAdoMapping,
  adoIntegrationService,
} from '../../services/adoIntegrationService';

import { buildBacklogMonitoringLink } from './adoLinks';

/** Max events fetched per refresh of the recent-activity window. */
const DEFAULT_EVENT_LIMIT = 200;
/** Max digests requested when loading the latest rollups. */
const DEFAULT_DIGEST_LIMIT = 10;
/** Shown in the deep link, e.g. ``https://dev.azure.com``. */
const ADO_BASE_URL = 'https://dev.azure.com';
/** Rolling window of recent activity surfaced by the panel, in days. */
const LOOKBACK_DAYS = 7;
/** Milliseconds in a single day, used to compute the window start. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface BacklogMonitoringPanelProps {
  projectId: number;
  mapping: ProjectAdoMapping;
}

interface PanelState {
  loading: boolean;
  error: string | null;
  events: BacklogEvent[];
  counts: BacklogEventCounts;
  cursor: BacklogCursor | null;
  digest: BacklogDigest | null;
  lastCheckedAt: string | null;
  busy: 'check' | 'dismiss' | 'markSeen' | null;
  filters: BacklogFilters;
  monitoringEnabled: boolean;
}

/** Server-side noise filters applied to the events feed. */
interface BacklogFilters {
  /** Ignore Archie's own ADO imports (``codie-source:archie``). */
  excludeArchie: boolean;
  /** Ignore every item tagged with a ``codie``-prefixed tag. */
  excludeCodie: boolean;
}

/** Default filters: hide Archie imports, keep other Codie changes. */
const DEFAULT_FILTERS: BacklogFilters = {
  excludeArchie: true,
  excludeCodie: false,
};

const EMPTY_COUNTS: BacklogEventCounts = {
  total: 0,
  added: 0,
  updated: 0,
  deleted: 0,
};

const initialPanelState: PanelState = {
  loading: true,
  error: null,
  events: [],
  counts: EMPTY_COUNTS,
  cursor: null,
  digest: null,
  lastCheckedAt: null,
  busy: null,
  filters: DEFAULT_FILTERS,
  monitoringEnabled: true,
};

/** Localized, sortable label for the events list grouping. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString();
}

/** Start (ISO) of the rolling recent-activity window. */
function windowStartIso(): string {
  return new Date(Date.now() - LOOKBACK_DAYS * MS_PER_DAY).toISOString();
}

/** True when an event is newer than the persisted "seen" cursor. */
function isNewEvent(
  evt: BacklogEvent,
  cursor: BacklogCursor | null,
): boolean {
  const seenAt = cursor?.last_seen_at;
  if (!seenAt) {
    return true;
  }
  return new Date(evt.receivedAt).getTime() > new Date(seenAt).getTime();
}

function buildWorkItemLink(
  adoOrg: string,
  adoProject: string,
  workItemId: number | null | undefined,
): string | null {
  if (!workItemId) {
    return null;
  }
  return (
    `${ADO_BASE_URL}/${encodeURIComponent(adoOrg)}` +
    `/${encodeURIComponent(adoProject)}/_workitems/edit/${workItemId}`
  );
}

function describeEvent(evt: BacklogEvent): string {
  if (evt.summary) {
    return evt.summary;
  }
  const what = evt.workItemType ?? 'Work item';
  const id = evt.workItemId ? `#${evt.workItemId}` : '';
  const verb = evt.eventKind.split('.').slice(-1)[0] ?? 'changed';
  return `${what} ${id} ${verb}`.trim();
}

function pickHeadlineDigest(items: BacklogDigest[]): BacklogDigest | null {
  if (items.length === 0) {
    return null;
  }
  const unread = items.find((d) => !d.consumedAt && d.significant);
  if (unread) {
    return unread;
  }
  const significant = items.find((d) => d.significant);
  return significant ?? items[0];
}

function errorToMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  return 'Failed to fetch backlog updates.';
}

const CountBadge: React.FC<{ label: string; value: number; tone: string }> = ({
  label,
  value,
  tone,
}) => (
  <span
    className={`badge ${tone} badge-sm font-semibold`}
    aria-label={`${value} ${label}`}
  >
    {label}: {value}
  </span>
);

/**
 * Per-project "What's new in ADO" panel.
 *
 * Polls Codie's backlog-events feed on demand, persists a per-project
 * cursor server-side, surfaces a digest card the user can dismiss,
 * and links each event back into Azure DevOps.
 */
const BacklogMonitoringPanel: React.FC<BacklogMonitoringPanelProps> = ({
  projectId,
  mapping,
}) => {
  const [state, setState] = useState<PanelState>(initialPanelState);

  const applyEventsResponse = useCallback(
    (res: BacklogEventsResponse, cursor: BacklogCursor | null) => {
      setState((s) => ({
        ...s,
        events: res.items,
        counts: res.counts,
        cursor,
        lastCheckedAt: new Date().toISOString(),
        loading: false,
        busy: null,
        error: null,
        monitoringEnabled: res.monitoringEnabled,
      }));
    },
    [],
  );

  const loadAll = useCallback(
    async (mode: 'initial' | 'check', filters: BacklogFilters) => {
      setState((s) => ({
        ...s,
        loading: mode === 'initial',
        busy: mode === 'check' ? 'check' : s.busy,
        error: null,
        filters,
      }));
      try {
        const [eventsRes, digestsRes, cursor] = await Promise.all([
          adoIntegrationService.listBacklogEvents(projectId, {
            since: windowStartIso(),
            limit: DEFAULT_EVENT_LIMIT,
            excludeArchie: filters.excludeArchie,
            excludeCodie: filters.excludeCodie,
            advanceCursor: false,
          }),
          adoIntegrationService.listBacklogDigests(projectId, {
            limit: DEFAULT_DIGEST_LIMIT,
            significantOnly: false,
          }),
          adoIntegrationService.getBacklogCursor(projectId),
        ]);
        applyEventsResponse(eventsRes, cursor);
        setState((s) => ({
          ...s,
          digest: pickHeadlineDigest(digestsRes.items),
        }));
      } catch (err) {
        setState((s) => ({
          ...s,
          loading: false,
          busy: null,
          error: errorToMessage(err),
        }));
      }
    },
    [projectId, applyEventsResponse],
  );

  useEffect(() => {
    loadAll('initial', DEFAULT_FILTERS);
  }, [loadAll]);

  const handleDismissDigest = useCallback(async () => {
    if (!state.digest) {
      return;
    }
    setState((s) => ({ ...s, busy: 'dismiss', error: null }));
    try {
      await adoIntegrationService.ackBacklogDigest(
        projectId,
        state.digest.id,
      );
      setState((s) => ({ ...s, digest: null, busy: null }));
    } catch (err) {
      setState((s) => ({
        ...s,
        busy: null,
        error: errorToMessage(err),
      }));
    }
  }, [projectId, state.digest]);

  const handleMarkSeen = useCallback(async () => {
    setState((s) => ({ ...s, busy: 'markSeen', error: null }));
    try {
      const cursor = await adoIntegrationService.markBacklogSeen(projectId);
      setState((s) => ({ ...s, cursor, busy: null }));
    } catch (err) {
      setState((s) => ({
        ...s,
        busy: null,
        error: errorToMessage(err),
      }));
    }
  }, [projectId]);

  const handleToggleFilter = useCallback(
    (key: keyof BacklogFilters) => {
      const filters = {
        ...state.filters,
        [key]: !state.filters[key],
      };
      loadAll('check', filters);
    },
    [state.filters, loadAll],
  );

  const groupedEvents = useMemo(() => {
    const buckets = new Map<string, BacklogEvent[]>();
    for (const evt of state.events) {
      const key = dayKey(evt.receivedAt);
      const bucket = buckets.get(key) ?? [];
      bucket.push(evt);
      buckets.set(key, bucket);
    }
    return Array.from(buckets.entries());
  }, [state.events]);

  const newCount = useMemo(
    () => state.events.filter((e) => isNewEvent(e, state.cursor)).length,
    [state.events, state.cursor],
  );

  const newBadgeTone = newCount > 0 ? 'badge-primary' : 'badge-ghost';

  const monitoringEnabled = state.monitoringEnabled;
  const monitoringOff = !monitoringEnabled && !state.loading;

  return (
    <div className="card bg-base-100 border border-base-300 shadow-sm">
      <div className="card-body gap-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="card-title text-lg flex items-center gap-2">
              What's new in ADO
              {monitoringEnabled && (
                <span
                  className={`badge ${newBadgeTone} badge-sm`}
                  aria-label={`${newCount} new backlog changes`}
                >
                  {newCount} new
                </span>
              )}
            </h3>
            {monitoringEnabled && (
              <p className="text-xs text-text-onSurface-subtle">
                Backlog activity on{' '}
                <strong>
                  {mapping.ado_org}/{mapping.ado_project}
                </strong>{' '}
                in the last {LOOKBACK_DAYS} days.
              </p>
            )}
          </div>
          {monitoringEnabled && (
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => { loadAll('check', state.filters); }}
                disabled={state.busy !== null || state.loading}
              >
                {state.busy === 'check' ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => { handleMarkSeen(); }}
                disabled={
                  state.busy !== null || state.loading || newCount === 0
                }
                title="Mark all current activity as seen"
              >
                {state.busy === 'markSeen' ? 'Marking…' : 'Mark all as seen'}
              </button>
            </div>
          )}
        </div>

        {monitoringOff && (
          <div className="alert bg-system-subtle-info text-text-onSurface-strong text-sm flex items-start gap-3">
            <div>
              <div className="font-semibold">
                Backlog monitoring is turned off
              </div>
              <p className="text-xs mt-1">
                Codie isn't tracking backlog changes for{' '}
                <strong>
                  {mapping.ado_org}/{mapping.ado_project}
                </strong>
                . Turn it on in the Codie extension to see what's new in
                ADO here.
              </p>
              <a
                className="link link-primary text-xs"
                href={buildBacklogMonitoringLink(
                  mapping.ado_org,
                  mapping.ado_project,
                )}
                target="_blank"
                rel="noreferrer noopener"
              >
                Enable backlog monitoring in ADO ↗
              </a>
            </div>
          </div>
        )}

        {monitoringEnabled && state.lastCheckedAt && (
          <div className="text-xs text-text-onSurface-subtle">
            Last refreshed {new Date(state.lastCheckedAt).toLocaleString()}.
            {state.cursor?.last_seen_at && (
              <>
                {' '}Seen up to{' '}
                {new Date(state.cursor.last_seen_at).toLocaleString()}.
              </>
            )}
          </div>
        )}

        {monitoringEnabled && (
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
            role="group"
            aria-label="Backlog noise filters"
          >
            <label className="label cursor-pointer justify-start gap-2 p-0">
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={state.filters.excludeArchie}
                onChange={() => { handleToggleFilter('excludeArchie'); }}
                disabled={state.busy !== null || state.loading}
              />
              <span className="label-text text-xs">
                Ignore Archie's ADO imports
              </span>
            </label>
            <label className="label cursor-pointer justify-start gap-2 p-0">
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={state.filters.excludeCodie}
                onChange={() => { handleToggleFilter('excludeCodie'); }}
                disabled={state.busy !== null || state.loading}
              />
              <span className="label-text text-xs">
                Ignore all Codie changes (tags starting with “codie”)
              </span>
            </label>
          </div>
        )}

        {monitoringEnabled && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Backlog event counts">
            <CountBadge label="added" value={state.counts.added} tone="badge-success" />
            <CountBadge label="updated" value={state.counts.updated} tone="badge-info" />
            <CountBadge label="deleted" value={state.counts.deleted} tone="badge-warning" />
          </div>
        )}

        {state.error && (
          <div className="alert alert-error text-sm py-2">
            <span>{state.error}</span>
          </div>
        )}

        {monitoringEnabled && state.digest && (
          <div className="alert bg-system-subtle-info text-text-onSurface-strong text-sm flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-semibold">
                {state.digest.headline ?? 'Latest digest from Codie'}
              </div>
              {state.digest.reasons && state.digest.reasons.length > 0 && (
                <ul className="list-disc ml-5 mt-1 text-xs">
                  {state.digest.reasons.slice(0, 4).map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => { handleDismissDigest(); }}
              disabled={state.busy === 'dismiss'}
            >
              {state.busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
            </button>
          </div>
        )}

        {state.loading && (
          <div className="text-sm text-text-onSurface-subtle">
            Loading backlog activity…
          </div>
        )}

        {monitoringEnabled &&
          !state.loading &&
          groupedEvents.length === 0 &&
          !state.error && (
          <div className="rounded-md bg-background-section px-4 py-3 text-sm text-text-onSurface-subtle">
              No backlog changes in the last {LOOKBACK_DAYS} days.
          </div>
        )}

        {monitoringEnabled && groupedEvents.length > 0 && (
          <div className="space-y-3">
            {groupedEvents.map(([day, items]) => (
              <div key={day}>
                <div className="text-xs font-semibold uppercase tracking-wide text-text-onSurface-subtle mb-1">
                  {day}
                </div>
                <ul className="space-y-1">
                  {items.map((evt) => {
                    const link = buildWorkItemLink(
                      evt.adoOrg,
                      evt.adoProject,
                      evt.workItemId,
                    );
                    return (
                      <li
                        key={evt.id}
                        className="rounded border border-border-medium bg-background-surface px-3 py-2 text-sm flex items-start justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate flex items-center gap-2">
                            {isNewEvent(evt, state.cursor) && (
                              <span className="badge badge-primary badge-xs shrink-0">
                                new
                              </span>
                            )}
                            <span className="truncate">
                              {describeEvent(evt)}
                            </span>
                          </div>
                          <div className="text-xs text-text-onSurface-subtle">
                            {evt.eventKind}
                            {evt.changedBy ? ` · ${evt.changedBy}` : ''}
                            {evt.state ? ` · ${evt.state}` : ''}
                          </div>
                        </div>
                        {link && (
                          <a
                            className="link link-primary text-xs shrink-0"
                            href={link}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            Open in ADO ↗
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BacklogMonitoringPanel;
